"use client";

import { useMemo, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import type {
  FacetBucket,
  FacetMatchMode,
} from "modules/collections/details/model/collection";
import type { GameId } from "modules/common/model/models";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import { FacetCheckbox } from "./FacetCheckbox";
import { GameDivider, GameGroupWrapper } from "./FacetGameDivider";
import { formatFacetLabel } from "./facetPresentation";
import { cn } from "utils/utils";

const INITIAL_SHOW_COUNT = 10;

interface FacetSearchableGroupProps {
  title: string;
  buckets: FacetBucket[];
  selected: Array<string | number>;
  onToggle: (value: string | number, game?: string) => void;
  defaultOpen?: boolean;
  matchMode?: FacetMatchMode;
  onMatchModeChange?: (mode: FacetMatchMode) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  gameExclusive?: GameId;
  onClear?: () => void;
}

export function FacetSearchableGroup({
  title,
  buckets,
  selected,
  onToggle,
  defaultOpen = false,
  matchMode,
  onMatchModeChange,
  isOpen: controlledIsOpen,
  onToggleOpen,
  gameExclusive,
  onClear,
}: FacetSearchableGroupProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen ?? internalOpen;
  const handleToggle = onToggleOpen ?? (() => setInternalOpen((o) => !o));
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const activeCount = selected.length;

  const activeValues = useMemo(() => new Set(selected.map(String)), [selected]);

  const withPlaceholders = useMemo(() => {
    const safe = buckets ?? [];
    if (activeValues.size === 0) return safe;

    const existing = new Set(safe.map((b) => String(b.value)));
    const placeholders: FacetBucket[] = [];
    for (const val of activeValues) {
      if (!existing.has(val)) {
        placeholders.push({ value: val, count: 0 });
      }
    }
    return placeholders.length > 0 ? [...placeholders, ...safe] : safe;
  }, [buckets, activeValues]);

  const filtered = useMemo(() => {
    if (!search) return withPlaceholders;
    const q = search.toLowerCase();
    return withPlaceholders.filter(
      (b) =>
        String(b.value).toLowerCase().includes(q) ||
        (b.label && b.label.toLowerCase().includes(q))
    );
  }, [withPlaceholders, search]);

  // Pin selected items to the top so they're always visible without expanding
  const sorted = useMemo(() => {
    if (activeValues.size === 0) return filtered;
    const pinned: FacetBucket[] = [];
    const rest: FacetBucket[] = [];
    for (const b of filtered) {
      if (activeValues.has(String(b.value))) {
        pinned.push(b);
      } else {
        rest.push(b);
      }
    }
    return [...pinned, ...rest];
  }, [filtered, activeValues]);

  const displayed = useMemo(
    () => (showAll || search ? sorted : sorted.slice(0, INITIAL_SHOW_COUNT)),
    [sorted, showAll, search]
  );

  const gameGroups = useMemo(() => {
    const gamesPresent = new Set(
      displayed.filter((b) => b.game).map((b) => b.game!)
    );
    if (gamesPresent.size <= 1) return null;

    const groups: { game: string; buckets: FacetBucket[] }[] = [];
    const byGame = new Map<string, FacetBucket[]>();
    for (const b of displayed) {
      const g = b.game ?? "";
      if (!byGame.has(g)) byGame.set(g, []);
      byGame.get(g)!.push(b);
    }
    for (const [game, items] of byGame) {
      groups.push({ game, buckets: items });
    }
    return groups;
  }, [displayed]);

  return (
    <div className="border-base-content/10 border-b last:border-b-0">
      <div className="hover:bg-base-300 flex items-center transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm font-medium"
          onClick={handleToggle}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          />
          <span className="flex-1 text-left">{title}</span>
          {gameExclusive && (
            <span className="text-base-content/40 flex items-center">
              <GameIcon game={gameExclusive} />
            </span>
          )}
          {activeCount > 0 && (
            <span className="badge badge-primary badge-xs">{activeCount}</span>
          )}
        </button>
        {activeCount > 0 && onClear && (
          <button
            type="button"
            className="text-base-content/50 hover:text-error pr-2 text-xs transition-colors"
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-1 px-1 pb-2">
            {!!onMatchModeChange && activeCount >= 1 && (
              <div className="mb-1 flex items-center gap-1 px-1">
                <span className="text-base-content/50 text-xs">Match:</span>
                <div className="join join-horizontal">
                  <button
                    type="button"
                    className={`btn join-item btn-xs ${(matchMode ?? "or") === "or" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => onMatchModeChange("or")}
                  >
                    Any
                  </button>
                  <button
                    type="button"
                    className={`btn join-item btn-xs ${matchMode === "and" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => onMatchModeChange("and")}
                  >
                    All
                  </button>
                </div>
              </div>
            )}
            <label
              className={cn(
                "input input-bordered input-xs my-1.5 flex items-center gap-1 px-1.5",
                "focus:outline-base-content/50 focus-within:outline-base-content/50",
                "focus:border-base-content/50 focus-within:border-base-content/50"
              )}
            >
              <Search size={12} className="text-base-content/40 shrink-0" />
              <input
                type="text"
                className="grow bg-transparent outline-none"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowAll(false);
                }}
              />
              {search && (
                <button
                  type="button"
                  className="text-base-content/40 hover:text-base-content shrink-0"
                  onClick={() => setSearch("")}
                >
                  <X size={12} />
                </button>
              )}
            </label>

            <div
              className={`flex flex-col gap-0.5 ${
                showAll ? "max-h-64 overflow-y-auto" : ""
              }`}
            >
              {gameGroups
                ? gameGroups.map(({ game, buckets: gameBuckets }) => (
                    <div key={game}>
                      <GameDivider game={game} />
                      <GameGroupWrapper game={game}>
                        {gameBuckets.map((bucket) => (
                          <FacetCheckbox
                            key={`${game}-${String(bucket.value)}`}
                            label={
                              bucket.label ?? formatFacetLabel(bucket.value)
                            }
                            count={bucket.count}
                            checked={selected.includes(bucket.value)}
                            disabled={
                              bucket.count === 0 &&
                              !selected.includes(bucket.value)
                            }
                            onToggle={onToggle}
                            value={bucket.value}
                            game={game}
                          />
                        ))}
                      </GameGroupWrapper>
                    </div>
                  ))
                : displayed.map((bucket) => (
                    <FacetCheckbox
                      key={String(bucket.value)}
                      label={bucket.label ?? formatFacetLabel(bucket.value)}
                      count={bucket.count}
                      checked={selected.includes(bucket.value)}
                      disabled={
                        bucket.count === 0 && !selected.includes(bucket.value)
                      }
                      onToggle={onToggle}
                      value={bucket.value}
                    />
                  ))}
            </div>

            {!search && sorted.length > INITIAL_SHOW_COUNT && (
              <button
                type="button"
                className="text-primary px-1 text-left text-xs hover:underline"
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll ? "Show less" : `Show all (${sorted.length})`}
              </button>
            )}

            {displayed.length === 0 && (
              <span className="text-base-content/40 px-1 py-1 text-xs">
                {search ? "No matches" : "No values available"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
